import React from 'react';
import { Droppable } from '@hello-pangea/dnd';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';

// Module Components
import PreviewBlock from './PreviewBlock';
import RegionHeader from './RegionHeader';

// Constants
import {
  CARD_BLOCK_DND_TYPE,
  IMAGE_FIELD_ID,
  PHOTO_ANCHORS,
  STRINGS,
  getFieldClassName,
  getFieldDefinition,
  photoAnchorDroppableId,
} from '../../../constants';

// Types
import {
  BlockPlacement,
  CardOrientation,
  FieldId,
  FieldConfiguration,
  ImageFit,
  PhotoAnchor,
  RegionId,
} from '../../../types';

interface PhotoRegionProps {
  // Read-only: these arrays are shared with the per-document index in
  // state/cardIndex, which is what makes them stable across renders.
  blocksByAnchor: Readonly<Record<PhotoAnchor, readonly BlockPlacement[]>>;
  imageEnabled: boolean;
  /** How the image fills the region — the image's own property (spec R46). */
  imageFit: ImageFit;
  orientation: CardOrientation;
  isDragging: boolean;
  activeRegion: RegionId | null;
  selectedFieldId: FieldId | null;
  configurationFor: (fieldId: FieldId) => FieldConfiguration;
  /** Card-wide: price rows carry the unit of measure (spec R69, R72). */
  onSelectBlock: (fieldId: FieldId) => void;
  onDeleteBlock: (fieldId: FieldId) => void;
}

/**
 * Anchor slots are positioned with fractional and positive offsets only — this
 * repo's Tailwind prefix makes negative utilities easy to get silently wrong, so
 * none are used. The corners sit clear of the bottom strip on purpose: two
 * overlapping drop boxes would make the library's hit-testing ambiguous.
 */
const anchorPositionClass: Record<PhotoAnchor, string> = {
  'top-left': 'tw-top-2 tw-left-2 tw-items-start',
  'top-right': 'tw-top-2 tw-right-2 tw-items-end',
  'bottom-left': 'tw-bottom-10 tw-left-2 tw-items-start',
  'bottom-right': 'tw-bottom-10 tw-right-2 tw-items-end',
  center: 'tw-top-1/3 tw-bottom-1/3 tw-left-1/3 tw-right-1/3 tw-items-center tw-justify-center',
  'bottom-strip': 'tw-bottom-0 tw-left-0 tw-right-0 tw-h-8 tw-items-center',
};

/**
 * Fit / Fill / Stretch, expressed the way the browser already expresses it for a
 * graphic with an intrinsic aspect ratio (spec R46).
 *
 * The preview has no real product photograph, so the stand-in has to *show* the
 * difference rather than assert it: `meet` letterboxes the graphic inside the
 * frame, `slice` scales it up and crops, `none` distorts it to the frame's shape —
 * which is exactly what the three options do to a real image.
 */
const IMAGE_FIT_ASPECT: Record<ImageFit, string> = {
  fit: 'xMidYMid meet',
  fill: 'xMidYMid slice',
  stretch: 'none',
};

/**
 * The image stand-in — and, since 2026-09-29, the Primary Image's click target.
 *
 * The image is the only field with no `PreviewBlock`, because it has no
 * placement: it IS the photo region. That is why clicking it in the preview did
 * nothing while every other field opened its properties on click (R55), and the
 * image's own panel — Image display, Custom styling — could only be reached
 * through the field rail's chevron.
 *
 * The handler sits HERE rather than on the frame because the frame also holds
 * the six anchor droppables and the blocks inside them. A `role="button"`
 * wrapping those would put interactive content inside a button, and an overlay
 * block's own click would have to fight its way out. This layer is `inset-0`
 * under the anchors, so a click on the image reaches it and a click on a block
 * does not (that block stops its own propagation, as it always has).
 *
 * The selection ring is `ring-inset`, unlike `PreviewBlock`'s offset ring: the
 * frame above this clips to `overflow-hidden`, so an outset ring — and an
 * outline, and a focus ring — would be cut off. Drawing it inside the box is
 * what makes it visible at all, and on a filled area this size it reads the
 * same.
 *
 * Only selectable while the image is enabled, matching the rail: a disabled
 * field shows no properties affordance there either, and unticking one closes
 * the panel (`inspector.dismiss`).
 */
const ImagePlaceholder = ({
  dimmed,
  imageFit,
  isSelected,
  onSelect,
}: {
  dimmed: boolean;
  imageFit: ImageFit;
  isSelected: boolean;
  onSelect: (() => void) | null;
}) => (
  <div
    role={onSelect ? 'button' : undefined}
    tabIndex={onSelect ? 0 : undefined}
    aria-label={onSelect ? getFieldDefinition(IMAGE_FIELD_ID).name : undefined}
    aria-pressed={onSelect ? isSelected : undefined}
    onClick={
      onSelect
        ? (event) => {
            // The card wrapper clears the selection on click (R58); without
            // this, selecting the image would immediately deselect it.
            event.stopPropagation();
            onSelect();
          }
        : undefined
    }
    onKeyDown={
      onSelect
        ? (event) => {
            // Both keys: this is a plain button, not a drag handle, so unlike
            // `PreviewBlock` there is no lift for Space to belong to.
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onSelect();
            }
          }
        : undefined
    }
    className={`tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-bg-surface-sunken ${
      dimmed ? 'tw-opacity-40' : ''
    } ${
      onSelect
        ? `tw-cursor-pointer focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-accent ${
            isSelected
              ? 'tw-ring-2 tw-ring-inset tw-ring-accent'
              : 'hover:tw-ring-1 hover:tw-ring-inset hover:tw-ring-edge-strong'
          }`
        : ''
    }`}
  >
    <svg
      viewBox="0 0 48 36"
      preserveAspectRatio={IMAGE_FIT_ASPECT[imageFit]}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      /* contrast-ok(non-text): decorative placeholder art. It is aria-hidden,
         and the "Product image" caption rendered beside it in grey-600 carries
         the meaning — so WCAG 1.4.11 does not apply to the glyph itself. */
      className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-text-ink-disabled"
    >
      <rect x="4" y="4" width="40" height="28" rx="2" />
      <circle cx="16" cy="14" r="3" />
      <path d="M10 30l10-10 8 8 6-6 8 8" />
    </svg>

    <Typography
      variant="caption-metadata"
      as="span"
      className="tw-relative tw-px-1 tw-rounded-sm tw-bg-surface-sunken tw-text-ink-tertiary"
    >
      {STRINGS.preview.imagePlaceholder}
    </Typography>
  </div>
);

/**
 * The Photo region: the product image plus six anchored overlay slots
 * (spec R48, R52). Each anchor is its own drop target, so a drop lands exactly
 * where the user aimed instead of being inferred from pointer geometry.
 *
 * The image itself is selectable (R55) even though it has no block — see
 * `ImagePlaceholder`, which carries that.
 */
const PhotoRegion = ({
  blocksByAnchor,
  imageEnabled,
  imageFit,
  orientation,
  isDragging,
  activeRegion,
  selectedFieldId,
  configurationFor,
  onSelectBlock,
  onDeleteBlock,
}: PhotoRegionProps) => {
  const sizeClass =
    orientation === 'horizontal' ? 'tw-w-36 tw-h-36 tw-shrink-0' : 'tw-w-full tw-h-48';

  return (
    <div className={orientation === 'horizontal' ? 'tw-shrink-0' : ''}>
      <RegionHeader
        region="photo"
        isDragging={isDragging}
        isActive={activeRegion === 'photo'}
      />

      {/* The image frame carries the image element's own selector class, so the
          Primary Image's Custom styling section has something to style — it has no
          block of its own (spec R67). */}
      <div
        className={`${getFieldClassName(
          IMAGE_FIELD_ID
        )} tw-relative tw-overflow-hidden tw-rounded-sm ${sizeClass}`}
      >
        <ImagePlaceholder
          dimmed={!imageEnabled}
          imageFit={imageFit}
          isSelected={selectedFieldId === IMAGE_FIELD_ID}
          onSelect={imageEnabled ? () => onSelectBlock(IMAGE_FIELD_ID) : null}
        />

        {PHOTO_ANCHORS.map((anchor) => {
          const blocks = blocksByAnchor[anchor.id];
          return (
            <Droppable
              key={anchor.id}
              droppableId={photoAnchorDroppableId(anchor.id)}
              type={CARD_BLOCK_DND_TYPE}
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`tw-absolute tw-flex tw-flex-col tw-gap-1 tw-min-w-[32px] tw-min-h-[32px] tw-max-w-full tw-rounded-sm tw-p-0.5 ${
                    anchorPositionClass[anchor.id]
                  } ${
                    snapshot.isDraggingOver
                      ? 'tw-bg-accent-tint tw-outline tw-outline-2 tw-outline-accent'
                      : isDragging
                        ? 'tw-outline-dashed tw-outline-1 tw-outline-accent'
                        : ''
                  } ${isDragging || blocks.length > 0 ? '' : 'tw-pointer-events-none'}`}
                >
                  {blocks.map((placement, index) => (
                    <PreviewBlock
                      key={placement.fieldId}
                      placement={placement}
                      configuration={configurationFor(placement.fieldId)}
                      index={index}
                      isSelected={selectedFieldId === placement.fieldId}
                      onSelect={() => onSelectBlock(placement.fieldId)}
                      onDelete={() => onDeleteBlock(placement.fieldId)}
                    />
                  ))}
                  {provided.placeholder}

                  {isDragging && blocks.length === 0 && (
                    <Typography
                      variant="caption-metadata"
                      as="span"
                      className="tw-text-accent tw-whitespace-nowrap"
                    >
                      {anchor.label}
                    </Typography>
                  )}
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </div>
  );
};

export default PhotoRegion;
